import dayjs from 'dayjs';
import {useEffect, useMemo, useRef, useState} from 'react';
import {FiCheck, FiChevronDown, FiClock, FiMinusCircle} from 'react-icons/fi';
import styled, {css} from 'styled-components';
import {goalDueDate} from '@utils/date';
import {parseCurrencyValue} from '@constants';
import {AtRiskIcon, BehindIcon, NewOnTrackIcon} from '@ui/atoms/icons';
import {goalStatusColors} from '@ui/style/goal-status';
import {
  avatarBackgroundColor,
  avatarForegroundColor,
  avatarHueFor,
} from '@ui/style/avatar-colors';
import {getColorFromTheme} from '@ui/ui-utils';
import {
  goalEndedAt,
  goalDisplayPerformance,
  goalIsEnded,
  goalIsOverdue,
  GoalDisplayPerformance,
  savedGoalPerformance,
  suggestedGoalPerformance,
  SuggestedGoalPerformance,
} from '@utils/goal-status';
import {
  GoalProgress,
  isMetricValueWithinStartBoundary,
  metricDirection,
  metricProgress,
} from '@utils/proportions';

type Performance = SuggestedGoalPerformance;
type DisplayPerformance = GoalDisplayPerformance;

const statusDetails: Record<
  DisplayPerformance,
  {label: string; color: string; icon: JSX.Element}
> = {
  on_track: {
    label: 'On track',
    color: goalStatusColors.on_track,
    icon: <NewOnTrackIcon />,
  },
  at_risk: {
    label: 'At risk',
    color: goalStatusColors.at_risk,
    icon: <AtRiskIcon />,
  },
  behind: {
    label: 'Behind',
    color: goalStatusColors.behind,
    icon: <BehindIcon />,
  },
  upcoming: {
    label: 'Upcoming',
    color: goalStatusColors.upcoming,
    icon: <FiClock />,
  },
  no_status: {
    label: 'No status',
    color: goalStatusColors.no_status,
    icon: <FiMinusCircle />,
  },
};

const performanceOptions: Performance[] = ['on_track', 'at_risk', 'behind'];

const buttonReset = css`
  border: 0;
  font-family: Graphik, sans-serif;
  cursor: pointer;
`;

const Header = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 24px;

  &::after {
    position: absolute;
    right: -24px;
    bottom: 0;
    left: -24px;
    height: 1px;
    background: ${getColorFromTheme('borderLight')};
    content: '';
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span<{$goal?: boolean}>`
  display: inline-flex;
  min-height: 28px;
  align-items: center;
  padding: 4px 8px;
  border-radius: 8px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${({$goal, theme}) =>
    $goal ? theme.colors.blue300 : theme.colors.textBody};
  font: 500 12px/20px Graphik, sans-serif;
`;

const GoalTitle = styled.h2`
  margin: 8px 0;
  color: ${getColorFromTheme('textDark')};
  font: 600 20px/28px Graphik, sans-serif;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  color: ${getColorFromTheme('textBody')};
  font: 400 13px/20px Graphik, sans-serif;
`;

const Member = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${getColorFromTheme('blackTwo')};
  font-weight: 500;
`;

const Avatar = styled.span<{$avatarHue?: number}>`
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  overflow: hidden;
  border-radius: 50%;
  background: ${({$avatarHue, theme}) =>
    $avatarHue === undefined
      ? theme.colors.backgroundDark
      : avatarBackgroundColor($avatarHue, theme.avatarTone)};
  color: ${({$avatarHue, theme}) =>
    $avatarHue === undefined
      ? theme.colors.textMedium
      : avatarForegroundColor($avatarHue, theme.avatarTone)};
  font: 500 11px/16px Graphik, sans-serif;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProgressRow = styled.div`
  display: grid;
  grid-template-columns: auto minmax(120px, 1fr) auto;
  align-items: end;
  gap: 16px;

  @media (max-width: 440px) {
    grid-template-columns: auto minmax(0, 1fr);
  }
`;

const ProgressValue = styled.strong`
  color: ${getColorFromTheme('blackTwo')};
  font: 600 24px/32px Graphik, sans-serif;
`;

const ProgressContent = styled.div`
  min-width: 0;
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/20px Graphik, sans-serif;
`;

const ProgressLabelRow = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ProgressTrack = styled.span`
  display: block;
  width: 100%;
  height: 10px;
  margin-top: 4px;
  overflow: hidden;
  border-radius: 8px;
  background: ${getColorFromTheme('borderLight')};
`;

const ProgressFill = styled.span<{$progress: number; $result?: boolean}>`
  display: block;
  width: ${({$progress}) => `${$progress}%`};
  height: 100%;
  border-radius: inherit;
  background: ${({$result, theme}) =>
    $result ? theme.colors.purple300 : theme.colors.goalProgressNeutral};
  transition: width 240ms ease;
`;

const StatusWrap = styled.div`
  position: relative;

  @media (max-width: 440px) {
    grid-column: 1 / -1;
  }
`;

const OverdueContext = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 0;
  color: ${getColorFromTheme('red400')};
  font: 400 12px/20px Graphik, sans-serif;

  strong {
    font-weight: 600;
  }
`;

const StatusButton = styled.button<{
  $color: string;
  $useBodyText?: boolean;
  $readOnly?: boolean;
}>`
  ${buttonReset};
  display: inline-flex;
  height: 34px;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: 1px solid ${({$color}) => $color};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${({$color, $useBodyText}) =>
    $useBodyText ? getColorFromTheme('textBody') : $color};
  font-size: 12px;
  font-weight: 500;

  svg {
    width: 16px;
    height: 16px;
  }

  &:disabled {
    cursor: ${({$readOnly}) => ($readOnly ? 'default' : 'wait')};
    opacity: ${({$readOnly}) => ($readOnly ? 1 : 0.65)};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

const StatusMenu = styled.div`
  position: absolute;
  z-index: 30;
  top: calc(100% + 8px);
  right: 0;
  width: 264px;
  padding: 12px 12px 8px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};
`;

const Suggestion = styled.p`
  margin: 0 0 12px;
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;

  strong {
    color: ${getColorFromTheme('textMedium')};
    font-weight: 500;
  }
`;

const StatusOption = styled.button`
  ${buttonReset};
  display: flex;
  width: 100%;
  min-height: 38px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px;
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textBody')};
  font-size: 14px;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('purple100')};
  }
`;

const StatusOptionLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const SuggestedTag = styled.span`
  padding: 4px 8px;
  border-radius: 8px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('textBody')};
  font: 600 12px/20px Graphik, sans-serif;
`;

const ResultsSection = styled.section`
  padding: 20px 0;
`;

const ResultsTitle = styled.h3`
  margin: 0;
  color: ${getColorFromTheme('textDark')};
  font: 600 14px/20px Graphik, sans-serif;
`;

const Result = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0 12px;

  & + & {
    padding-top: 12px;
  }
`;

const ResultTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: ${getColorFromTheme('blackThree')};
  font: 400 14px/22px Graphik, sans-serif;
`;

const ResultProgressRow = styled.div`
  display: flex;
  min-height: 32px;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  color: ${getColorFromTheme('textBody')};
  font: 500 14px/22px Graphik, sans-serif;

  strong {
    color: ${getColorFromTheme('blackTwo')};
    font-weight: 600;
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 15px;
  background: ${getColorFromTheme('borderDark')};
`;

const UpdateButton = styled.button`
  ${buttonReset};
  padding: 0;
  background: transparent;
  color: ${getColorFromTheme('purple300')};
  font-size: 14px;
  font-weight: 600;

  &:hover,
  &:focus-visible {
    outline: none;
    text-decoration: underline;
  }

  &:disabled {
    cursor: default;
    color: ${getColorFromTheme('textMuted')};
    text-decoration: none;
  }
`;

const ResultTrack = styled(ProgressTrack)`
  height: 4px;
  margin: 0;
`;

const Editor = styled.form`
  padding: 12px;
  border-radius: 8px;
  background: ${getColorFromTheme('backgroundLight')};
`;

const ValueField = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  color: ${getColorFromTheme('textBody')};
  font: 500 12px/18px Graphik, sans-serif;

  input {
    width: 100%;
    height: 38px;
    padding: 8px 10px;
    border: 1px solid ${getColorFromTheme('borderDark')};
    border-radius: 8px;
    outline: none;
    background: ${getColorFromTheme('white')};
    color: ${getColorFromTheme('blackTwo')};
    font: 400 14px/20px Graphik, sans-serif;
  }

  input:focus {
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 3px rgba(88, 90, 223, 0.12);
  }
`;

const ValueControls = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 8px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;

    > :first-child {
      grid-column: 1 / -1;
    }
  }
`;

const ValueHint = styled.span<{$error?: boolean}>`
  color: ${({$error, theme}) =>
    $error ? theme.colors.red400 : theme.colors.textBody};
  font: 400 12px/18px Graphik, sans-serif;
`;

const ValueSelectWrap = styled.div`
  position: relative;
  width: 100%;
`;

const ValueSelectButton = styled.button<{$open: boolean}>`
  ${buttonReset};
  display: flex;
  width: 100%;
  height: 38px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid
    ${({$open, theme}) =>
      $open ? theme.colors.purple300 : theme.colors.borderDark};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font: 400 14px/20px Graphik, sans-serif;
  text-align: left;

  svg {
    flex: 0 0 auto;
    color: ${getColorFromTheme('textBody')};
    transition: transform 160ms ease;
    transform: rotate(${({$open}) => ($open ? '180deg' : '0deg')});
  }

  &:focus-visible {
    outline: none;
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 3px rgba(88, 90, 223, 0.12);
  }
`;

const ValueSelectMenu = styled.div`
  position: absolute;
  z-index: 40;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  padding: 6px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};
`;

const ValueSelectOption = styled.button<{$active: boolean}>`
  ${buttonReset};
  display: flex;
  width: 100%;
  min-height: 36px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  background: ${({$active, theme}) =>
    $active ? theme.colors.purple100 : theme.colors.white};
  color: ${({$active, theme}) =>
    $active ? theme.colors.purple300 : theme.colors.blackTwo};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: left;
  transition: background-color 160ms ease, color 160ms ease;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('purple100')};
    color: ${getColorFromTheme('purple300')};
  }
`;

const EditorButton = styled.button<{$primary?: boolean}>`
  ${buttonReset};
  height: 38px;
  padding: 8px 12px;
  border: 1px solid
    ${({$primary, theme}) =>
      $primary ? theme.colors.purple300 : theme.colors.borderDark};
  border-radius: 8px;
  background: ${({$primary, theme}) =>
    $primary ? theme.colors.purple300 : theme.colors.white};
  color: ${({$primary, theme}) =>
    $primary ? theme.colors.alwaysWhite : theme.colors.textBody};
  font-size: 13px;
  font-weight: 600;

  &:disabled {
    cursor: not-allowed;
    border-color: ${getColorFromTheme('borderDark')};
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textMuted')};
  }
`;

const EmptyResults = styled.p`
  margin: 12px 0 0;
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
`;

const initials = (person: any) => {
  const first = person?.firstName?.[0] || '';
  const last = person?.lastName?.[0] || '';
  return `${first}${last}`.toUpperCase() || '—';
};

const memberName = (person: any) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(' ') ||
  person?.name ||
  'Unassigned';

const avatarUrl = (person: any) =>
  typeof person?.avatar === 'string' ? person.avatar : person?.avatar?.url;

const memberAvatarHue = (person: any) =>
  person
    ? avatarHueFor(person.id || person._id, memberName(person))
    : undefined;

const resultProgress = (result: any) =>
  metricProgress(result?.startValue, result?.currentValue, result?.targetValue);

const formatNumber = (value: unknown) =>
  new Intl.NumberFormat('en-GB', {maximumFractionDigits: 2}).format(
    Number(value) || 0,
  );

const resultValue = (result: any) => {
  const unit = String(result?.measurement?.unit || '').toLowerCase();
  if (unit === 'binary')
    return Number(result?.currentValue) === 1 ? 'Done' : 'Not done';
  const symbol =
    unit === 'currency'
      ? parseCurrencyValue(result?.measurement?.symbol || '')
      : '';
  const suffix = unit === 'percentage' ? '%' : '';
  return `${symbol}${formatNumber(
    result?.currentValue,
  )}${suffix} / ${symbol}${formatNumber(result?.targetValue)}${suffix}`;
};

export const GoalSidecarSummary = ({
  goal,
  onSave,
  canEdit = true,
  openStatusMenu = false,
}: {
  goal: any;
  onSave: (goal: any) => Promise<boolean>;
  canEdit?: boolean;
  openStatusMenu?: boolean;
}) => {
  const [open, setOpen] = useState(openStatusMenu);
  const [saving, setSaving] = useState(false);
  const [editingProgress, setEditingProgress] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [currentValue, setCurrentValue] = useState(
    String(goal?.currentValue ?? goal?.startValue ?? 0),
  );
  const menuRef = useRef<HTMLDivElement>(null);
  const performance = goalDisplayPerformance(goal);
  const savedPerformance = savedGoalPerformance(goal);
  const suggestion =
    performance === 'upcoming' ? undefined : suggestedGoalPerformance(goal);
  const selected = statusDetails[performance];
  const progress = GoalProgress(goal).percentage;
  const startValue = Number(goal?.startValue);
  const targetValue = Number(goal?.targetValue);
  const parsedCurrentValue = Number(currentValue);
  const savedCurrentValue = Number(goal?.currentValue ?? goal?.startValue ?? 0);
  const metricValueHasChanged = parsedCurrentValue !== savedCurrentValue;
  const metricValueIsValid = isMetricValueWithinStartBoundary(
    startValue,
    parsedCurrentValue,
    targetValue,
  );
  const direction = metricDirection(startValue, targetValue);
  const unit = String(goal?.measurement?.unit).toLowerCase();
  const minimumValue =
    direction === 'increase'
      ? unit === 'percentage'
        ? Math.max(0, startValue)
        : startValue
      : unit === 'percentage'
      ? 0
      : undefined;
  const maximumValue =
    direction === 'decrease'
      ? unit === 'percentage'
        ? Math.min(100, startValue)
        : startValue
      : unit === 'percentage'
      ? 100
      : undefined;
  const boundaryMessage =
    direction === 'decrease'
      ? `Enter ${formatNumber(
          startValue,
        )} or lower. This KPI starts at ${formatNumber(startValue)}.`
      : direction === 'increase'
      ? `Enter ${formatNumber(
          startValue,
        )} or higher. This KPI starts at ${formatNumber(startValue)}.`
      : `Enter ${formatNumber(
          startValue,
        )}. This KPI has the same start and target value.`;
  const assignee = goal?.assignee || goal?.createdBy;
  const ended = goalIsEnded(goal);
  const overdue = goalIsOverdue(goal);
  const endedAt = goalEndedAt(goal);
  const canUpdate = canEdit && !ended;
  const scope =
    String(goal?.goalType).toLowerCase() === 'company'
      ? 'Company-wide'
      : String(goal?.goalType).toLowerCase() === 'group'
      ? goal?.group?.[0]?.name || 'Team'
      : 'Personal';

  useEffect(() => {
    if (openStatusMenu && canUpdate) setOpen(true);
  }, [canUpdate, goal?.id, openStatusMenu]);

  useEffect(() => {
    if (canUpdate) return;
    setOpen(false);
    setEditingProgress(false);
  }, [canUpdate]);

  useEffect(() => {
    setCurrentValue(String(goal?.currentValue ?? goal?.startValue ?? 0));
  }, [goal?.currentValue, goal?.startValue]);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const updateStatus = async (nextPerformance: Performance) => {
    if (!canUpdate) return;
    if (nextPerformance === savedPerformance) {
      setOpen(false);
      return;
    }
    setSaving(true);
    setOpen(false);
    await onSave({...goal, performance: nextPerformance});
    setSaving(false);
  };

  const updateProgress = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextValue = Number(currentValue);
    if (
      !canUpdate ||
      !Number.isFinite(nextValue) ||
      !metricValueIsValid ||
      !metricValueHasChanged
    )
      return;

    setSavingProgress(true);
    const success = await onSave({
      ...goal,
      currentValue: nextValue,
      value: nextValue,
      valueUpdated: true,
      isUpdate: true,
    });
    setSavingProgress(false);
    if (success) setEditingProgress(false);
  };

  return (
    <Header>
      <div>
        <Tags>
          <Tag $goal>{goal?.isKpi ? 'KPI' : 'Goal'}</Tag>
          <Tag>{scope}</Tag>
          {goal?.goalAlignment && <Tag>Aligned to a parent goal</Tag>}
        </Tags>
        <GoalTitle>{goal?.name}</GoalTitle>
        <Meta>
          <Member>
            <Avatar
              aria-hidden="true"
              data-testid="goal-sidecar-owner-avatar"
              $avatarHue={memberAvatarHue(assignee)}>
              {avatarUrl(assignee) ? (
                <img src={avatarUrl(assignee)} alt="" />
              ) : (
                initials(assignee)
              )}
            </Avatar>
            {memberName(assignee)}
          </Member>
          {ended ? (
            endedAt && (
              <>
                <strong aria-hidden="true">·</strong>
                <span>Ended {dayjs(endedAt).format('MMM D, YYYY')}</span>
              </>
            )
          ) : (
            <>
              <span>
                {overdue && goal?.endDate
                  ? `Due ${dayjs(goal.endDate).format('MMM D, YYYY')}`
                  : goalDueDate(goal?.endDate, true, false, true)}
              </span>
              {goal?.createdAt && (
                <>
                  <strong aria-hidden="true">·</strong>
                  <span>
                    Created {dayjs(goal.createdAt).format('MMM D, YYYY')}
                  </span>
                </>
              )}
            </>
          )}
        </Meta>
      </div>

      <ProgressRow>
        <ProgressValue>{progress}%</ProgressValue>
        <ProgressContent>
          <ProgressLabelRow>
            <span>{goal?.isKpi ? resultValue(goal) : 'Avg. progress'}</span>
            {goal?.isKpi && canUpdate && (
              <UpdateButton
                type="button"
                disabled={editingProgress}
                onClick={() => setEditingProgress(true)}>
                Update progress
              </UpdateButton>
            )}
          </ProgressLabelRow>
          <ProgressTrack>
            <ProgressFill
              $progress={progress}
              data-testid="goal-sidecar-summary-progress"
              aria-hidden="true"
            />
          </ProgressTrack>
        </ProgressContent>
        <StatusWrap ref={menuRef}>
          <StatusButton
            type="button"
            $color={overdue ? goalStatusColors.behind : selected.color}
            $useBodyText={!overdue && performance === 'no_status'}
            $readOnly={!canUpdate}
            disabled={saving || !canUpdate}
            aria-haspopup={canUpdate ? 'menu' : undefined}
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}>
            {overdue ? <FiClock /> : selected.icon}
            {saving
              ? 'Updating…'
              : overdue
              ? canUpdate
                ? 'Review status'
                : 'Overdue'
              : selected.label}
            {canUpdate && <FiChevronDown aria-hidden="true" />}
          </StatusButton>
          {canUpdate && open && (
            <StatusMenu role="menu" aria-label="Change goal status">
              {overdue && (
                <Suggestion>
                  This goal remains overdue until its due date is extended or
                  the goal is ended.
                </Suggestion>
              )}
              {suggestion && (
                <Suggestion>
                  Suggested from progress vs. time:{' '}
                  <strong>{statusDetails[suggestion].label}</strong>
                </Suggestion>
              )}
              {performanceOptions.map((status) => (
                <StatusOption
                  key={status}
                  type="button"
                  role="menuitem"
                  onClick={() => updateStatus(status)}>
                  <StatusOptionLabel>
                    {statusDetails[status].icon}
                    {statusDetails[status].label}
                  </StatusOptionLabel>
                  {status === suggestion && (
                    <SuggestedTag>Suggested</SuggestedTag>
                  )}
                </StatusOption>
              ))}
            </StatusMenu>
          )}
        </StatusWrap>
      </ProgressRow>

      {overdue && (
        <OverdueContext>
          <FiClock aria-hidden="true" />
          <span>
            <strong>Overdue.</strong>{' '}
            {savedPerformance
              ? `Last marked ${statusDetails[
                  savedPerformance
                ].label.toLowerCase()}.`
              : 'No reported status yet.'}
          </span>
        </OverdueContext>
      )}

      {goal?.isKpi && canUpdate && editingProgress && (
        <Editor onSubmit={updateProgress}>
          <ValueField>
            New value
            <ValueControls>
              <input
                aria-label="New KPI value"
                type="number"
                min={minimumValue}
                max={maximumValue}
                step="any"
                value={currentValue}
                onChange={(event) => setCurrentValue(event.target.value)}
                aria-invalid={!metricValueIsValid}
                aria-describedby="kpi-progress-boundary"
                autoFocus
              />
              <EditorButton
                type="button"
                onClick={() => {
                  setCurrentValue(
                    String(goal?.currentValue ?? goal?.startValue ?? 0),
                  );
                  setEditingProgress(false);
                }}>
                Cancel
              </EditorButton>
              <EditorButton
                type="submit"
                $primary
                disabled={
                  savingProgress ||
                  currentValue.trim() === '' ||
                  Number.isNaN(Number(currentValue)) ||
                  !metricValueIsValid ||
                  !metricValueHasChanged
                }>
                {savingProgress ? 'Saving…' : 'Save'}
              </EditorButton>
            </ValueControls>
            <ValueHint
              id="kpi-progress-boundary"
              role={!metricValueIsValid ? 'alert' : undefined}
              $error={!metricValueIsValid}>
              {metricValueIsValid
                ? `Starts at ${formatNumber(
                    startValue,
                  )} and moves toward ${formatNumber(targetValue)}.`
                : boundaryMessage}
            </ValueHint>
          </ValueField>
        </Editor>
      )}
    </Header>
  );
};

const ResultRow = ({
  result,
  goal,
  onSave,
  canEdit,
}: {
  result: any;
  goal: any;
  onSave: (goal: any, resultId: string) => Promise<boolean>;
  canEdit: boolean;
}) => {
  const unit = String(result?.measurement?.unit || '').toLowerCase();
  const inputValue =
    unit === 'binary'
      ? Number(result?.currentValue) === 1
        ? '1'
        : '0'
      : String(result?.currentValue ?? 0);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState(inputValue);
  const [valueMenuOpen, setValueMenuOpen] = useState(false);
  const valueSelectRef = useRef<HTMLDivElement>(null);
  const progress = resultProgress(result);
  const assignee = result?.assignee;
  const parsedValue = Number(value);
  const valueHasChanged =
    Number.isFinite(parsedValue) && parsedValue !== Number(inputValue);

  useEffect(() => setValue(inputValue), [inputValue]);

  useEffect(() => {
    if (canEdit) return;
    setEditing(false);
    setValueMenuOpen(false);
  }, [canEdit]);

  useEffect(() => {
    if (!valueMenuOpen) return;
    const closeMenu = (event: MouseEvent) => {
      if (!valueSelectRef.current?.contains(event.target as Node)) {
        setValueMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, [valueMenuOpen]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canEdit || !valueHasChanged) return;
    setSaving(true);
    const nextValue = Number(value);
    const success = await onSave(
      {
        ...goal,
        keyResults: goal.keyResults.map((item: any) =>
          item.id === result.id ? {...item, currentValue: nextValue} : item,
        ),
      },
      result.id,
    );
    setSaving(false);
    if (success) setEditing(false);
  };

  return (
    <Result>
      <ResultTitleRow>
        <span>{result?.name}</span>
        <Avatar
          title={memberName(assignee)}
          data-testid="goal-sidecar-result-avatar"
          $avatarHue={memberAvatarHue(assignee)}>
          {avatarUrl(assignee) ? (
            <img src={avatarUrl(assignee)} alt="" />
          ) : (
            initials(assignee)
          )}
        </Avatar>
      </ResultTitleRow>

      <ResultProgressRow>
        <span>{resultValue(result)}</span>
        <strong>{Math.round(progress)}%</strong>
        {canEdit && (
          <>
            <Divider aria-hidden="true" />
            <UpdateButton
              type="button"
              disabled={editing}
              aria-disabled={editing}
              onClick={() => setEditing(true)}>
              Update progress
            </UpdateButton>
          </>
        )}
      </ResultProgressRow>
      <ResultTrack>
        <ProgressFill
          $progress={progress}
          $result
          data-testid="goal-sidecar-result-progress"
          aria-hidden="true"
        />
      </ResultTrack>

      {editing && (
        <Editor onSubmit={submit}>
          <ValueField>
            New value
            <ValueControls>
              {unit === 'binary' ? (
                <ValueSelectWrap ref={valueSelectRef}>
                  <ValueSelectButton
                    type="button"
                    $open={valueMenuOpen}
                    aria-label="New value"
                    aria-haspopup="listbox"
                    aria-expanded={valueMenuOpen}
                    onClick={() => setValueMenuOpen((open) => !open)}>
                    <span>{value === '1' ? 'Done' : 'Not done'}</span>
                    <FiChevronDown size={16} />
                  </ValueSelectButton>
                  {valueMenuOpen && (
                    <ValueSelectMenu role="listbox" aria-label="New value">
                      {[
                        {label: 'Not done', value: '0'},
                        {label: 'Done', value: '1'},
                      ].map((option) => (
                        <ValueSelectOption
                          key={option.value}
                          type="button"
                          role="option"
                          $active={value === option.value}
                          aria-selected={value === option.value}
                          onClick={() => {
                            setValue(option.value);
                            setValueMenuOpen(false);
                          }}>
                          <span>{option.label}</span>
                          {value === option.value && <FiCheck size={14} />}
                        </ValueSelectOption>
                      ))}
                    </ValueSelectMenu>
                  )}
                </ValueSelectWrap>
              ) : (
                <input
                  aria-label="New value"
                  type="number"
                  min={unit === 'percentage' ? 0 : undefined}
                  max={unit === 'percentage' ? 100 : undefined}
                  step="any"
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  autoFocus
                />
              )}
              <EditorButton
                type="button"
                onClick={() => {
                  setValue(inputValue);
                  setValueMenuOpen(false);
                  setEditing(false);
                }}>
                Cancel
              </EditorButton>
              <EditorButton
                type="submit"
                $primary
                disabled={
                  saving ||
                  value.trim() === '' ||
                  Number.isNaN(Number(value)) ||
                  !valueHasChanged
                }>
                {saving ? 'Saving…' : 'Save'}
              </EditorButton>
            </ValueControls>
          </ValueField>
        </Editor>
      )}
    </Result>
  );
};

export const GoalSidecarResults = ({
  goal,
  onSave,
  canEditResult = () => true,
}: {
  goal: any;
  onSave: (goal: any, resultId: string) => Promise<boolean>;
  canEditResult?: (result: any) => boolean;
}) => {
  const results = useMemo(() => goal?.keyResults || [], [goal?.keyResults]);
  const ended = goalIsEnded(goal);
  if (goal?.isKpi) return null;

  return (
    <ResultsSection>
      <ResultsTitle>Results</ResultsTitle>
      {results.length ? (
        results.map((result: any) => (
          <ResultRow
            key={result.id}
            result={result}
            goal={goal}
            onSave={onSave}
            canEdit={!ended && canEditResult(result)}
          />
        ))
      ) : (
        <EmptyResults>No results have been added yet.</EmptyResults>
      )}
    </ResultsSection>
  );
};
